import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import Tooltip from 'components/shared/Tooltip';
import Upload from 'hooks/useDirectUpload/Upload';
//since we export Attachment as the default export, we can import it as AttachmentModel
import AttachmentModel from 'models/Attachment';
import { updateAttachment } from 'services/api/attachments';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import RemoveAttachmentIcon from 'svg/RemoveAttachmentIcon';

import DocumentLink from './DocumentLink';
import DocumentTypeMenu from './DocumentTypeMenu';
import ProgressBar from './ProgressBar';

const AttachmentContainer = styled.div<{
  $error: boolean;
  $showUploadProgress: boolean;
}>`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.black3};
  border: 0.5px solid ${({ theme, $error }) => ($error ? theme.colors.red100 : theme.colors.black10)};
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  padding: ${({ $showUploadProgress }) => ($showUploadProgress ? '8px 16px' : '4px 16px')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 32px;
`;

const AttachmentInformation = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;

  > :first-child {
    flex: 1;
    width: 0;
  }
`;

const Filename = styled(BodySmallBold)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: 500;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const ErrorText = styled.span`
  font-style: italic;
  color: ${({ theme }) => theme.colors.red100};
  font-size: 12px;
  flex: 1 0 auto;
  margin-right: 8px;
`;

const ProgressContainer = styled.div`
  margin-top: 4px;
`;

const RemovedFile = styled.div`
  display: flex;
  align-items: center;
`;

// Flex, so the icon is not laid out as inline content sitting on the text baseline — that leaves
// descender space beneath it and reads as vertically off-centre in the row. InternalAttachment's
// delete control does the same.
const DeleteButton = styled.div`
  display: flex;
  align-items: center;
`;

const RemovedText = styled(BodySmall)`
  margin-left: 6px;
  color: ${colors.black50};
`;

type Props = {
  attachment: AttachmentModel | Upload;
  error?: boolean;
  disabled?: boolean;
  uploadProgress?: number;
  onDeleteAttachment?: (id: string) => void;
  canModifyDocumentType?: boolean;
  onDocumentTypeChange?: (id: string, docType: string | null) => void;
  // Which model owns the attachment, for the view link's signed-URL lookup (the API scopes it per
  // attachable). Defaults to Activity, so anything owned by something else has to say so.
  attachableModel?: string;
  // Defaults to the line trash icon (Figma "Icon_RemoveAttachment") — override only for a surface
  // that deliberately wants a different glyph (e.g. SmallTrashIcon).
  DeleteIcon?: React.ElementType<React.SVGProps<SVGSVGElement>>;
  // Lets a caller restyle the row with styled(ExternalAttachment) — e.g. a taller container — without
  // changing the default every other attachment surface renders at.
  className?: string;
};

export default function ExternalAttachment({
  attachment,
  error = false,
  disabled = false,
  uploadProgress,
  onDeleteAttachment,
  canModifyDocumentType = true,
  onDocumentTypeChange,
  attachableModel,
  DeleteIcon = RemoveAttachmentIcon,
  className,
}: Props) {
  const deletedByClient = attachment.fileDeletedByClient;
  const isRemoved = disabled || !!deletedByClient;
  // canModifyDocumentType gates the icon itself, not just the click. Without this a caller that
  // opts out still renders a tag menu whose items do nothing, because changeDocType is undefined.
  const showDocTypeIcon = canModifyDocumentType && !error && !uploadProgress && !isRemoved;
  const showUploadProgress = !!uploadProgress && !error;
  const deletedFileTooltip = `This file is no longer accessible due to ${deletedByClient?.name} file security policy`;

  const { mutate } = useMutation({
    mutationFn: updateAttachment,
  });

  const [activeDocType, setDocType] = useState(attachment.docType);
  useEffect(() => {
    setDocType(attachment.docType);
  }, [attachment.docType]);

  const changeDocType = canModifyDocumentType
    ? (onDocumentTypeChange ??
      ((id: string, docType: string | null) => {
        mutate(
          { id, docType },
          {
            onSuccess: () => {
              setDocType(docType);
            },
          }
        );
      }))
    : undefined;

  return (
    <AttachmentContainer className={className} $error={error} $showUploadProgress={showUploadProgress}>
      <AttachmentInformation>
        {isRemoved ? (
          <Tooltip text={deletedFileTooltip} disabled={!deletedByClient}>
            <RemovedFile>
              <RemoveAttachmentIcon color={colors.black50} />
              <RemovedText>This file has been removed</RemovedText>
            </RemovedFile>
          </Tooltip>
        ) : (
          <>
            <DocumentLink entry={attachment} attachableModel={attachableModel}>
              <Filename>{attachment.filename}</Filename>
            </DocumentLink>

            {error && <ErrorText>(Upload Error)</ErrorText>}

            {showDocTypeIcon && (
              <DocumentTypeMenu
                activeDocType={activeDocType}
                setActiveDocType={(s: string | null) => changeDocType?.(attachment.id, s)}
                disabled={disabled}
              />
            )}

            {onDeleteAttachment && (
              <DeleteButton>
                <Tooltip text='Remove Attachment'>
                  {/* No `fill` prop: RemoveAttachmentIcon strokes with currentColor, so `color` alone
                      drives it — a `fill` here would spread onto the <svg> and render a filled icon
                      solid instead (relevant if a caller overrides DeleteIcon with a filled glyph).

                      aria-label because the control is an icon with no text — it's the only
                      accessible name a screen reader (or a test) has to find "remove this file" by. */}
                  <DeleteIcon
                    aria-label='Remove Attachment'
                    onClick={() => onDeleteAttachment(attachment.id)}
                    color={colors.black100}
                    style={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              </DeleteButton>
            )}
          </>
        )}
      </AttachmentInformation>
      {showUploadProgress && (
        <ProgressContainer>
          <ProgressBar value={uploadProgress} showLabel={false} />
        </ProgressContainer>
      )}
    </AttachmentContainer>
  );
}
